'use client';

import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { useSwitchWorkspaceMutation } from '@/features/auth/api/authApi';
import { useBootstrapAuth } from '@/features/auth/hooks';
import { getSavedActiveContextTabUrl } from '@/features/context-tabs';
import {
  discardPersistedTimeTracker,
  useHasActivePersistedTimeTracker,
} from '@/features/time-entries/time-tracker';
import { useGetWorkspacesQuery } from '@/features/workspaces/api/workspacesApi';
import { setCurrentWorkspace } from '@/features/workspaces/slices/workspacesSlice';
import { baseApi } from '@/store/baseApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Avatar, ConfirmActionModal, SpeedDialMenu, Tooltip } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './WorkspaceSwitcher.module.scss';

import type { WorkspaceListItem } from '@/features/workspaces/types';

type WorkspaceSwitcherProps = {
  isSideNavExpanded: boolean;
  speedDialDirection?: 'up' | 'down' | 'left' | 'right';
};

function getWorkspaceAvatarSource(
  workspace?: {
    avatar?: string | null;
    avatar_url?: string | null;
    logo_url?: string | null;
  } | null,
) {
  return (
    workspace?.avatar_url ?? workspace?.logo_url ?? workspace?.avatar ?? null
  );
}

export function WorkspaceSwitcher({
  isSideNavExpanded,
  speedDialDirection = 'right',
}: WorkspaceSwitcherProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const bootstrapAuth = useBootstrapAuth();
  const currentWorkspace = useAppSelector((s) => s.workspaces.currentWorkspace);

  const { data, isFetching } = useGetWorkspacesQuery();
  const [switchWorkspace, { isLoading: isSwitchingWorkspace }] =
    useSwitchWorkspaceMutation();

  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<string | null>(
    null,
  );
  const [workspacePendingDiscard, setWorkspacePendingDiscard] =
    useState<WorkspaceListItem | null>(null);
  const hasActiveTimeTracker = useHasActivePersistedTimeTracker(
    currentWorkspace?.id,
  );

  const availableWorkspaces = useMemo(
    () => (data?.success ? data.data.results : []),
    [data],
  );
  const otherWorkspaces = useMemo(
    () =>
      availableWorkspaces.filter(
        (workspace: WorkspaceListItem) => workspace.id !== currentWorkspace?.id,
      ),
    [availableWorkspaces, currentWorkspace?.id],
  );
  const currentWorkspaceFromList = useMemo(
    () =>
      availableWorkspaces.find(
        (workspace: WorkspaceListItem) => workspace.id === currentWorkspace?.id,
      ) ?? null,
    [availableWorkspaces, currentWorkspace?.id],
  );

  const currentWorkspaceName = currentWorkspace?.name ?? 'Workspace';
  const currentWorkspaceAvatarSource =
    getWorkspaceAvatarSource(currentWorkspace) ??
    getWorkspaceAvatarSource(currentWorkspaceFromList);

  const executeSwitchWorkspace = async (workspace: WorkspaceListItem) => {
    if (isSwitchingWorkspace || pendingWorkspaceId) {
      return;
    }

    try {
      setPendingWorkspaceId(workspace.id);

      const response = await switchWorkspace({
        workspace_id: workspace.id,
      }).unwrap();

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Unable to switch workspace.');
      }

      const {
        user,
        workspace: switchedWorkspace,
        is_workspace_setup_required,
      } = response.data;

      bootstrapAuth({
        currentUser: user ?? null,
        currentWorkspaceSlug: switchedWorkspace?.slug ?? workspace.slug,
        isWorkspaceSetupRequired: is_workspace_setup_required ?? false,
        isWorkspaceOnboarded: switchedWorkspace?.is_workspace_onboarded ?? null,
      });
      dispatch(setCurrentWorkspace(switchedWorkspace ?? null));
      dispatch(baseApi.util.resetApiState());

      notify({
        type: 'success',
        message: response.message || `Switched to ${workspace.name}.`,
      });

      const nextSlug = switchedWorkspace?.slug ?? workspace.slug;
      const nextWorkspaceId = switchedWorkspace?.id ?? workspace.id;
      const restoredUrl = user?.id
        ? getSavedActiveContextTabUrl(user.id, nextWorkspaceId, nextSlug)
        : null;
      router.replace(restoredUrl ?? appRoutes.workspace.index(nextSlug));
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setPendingWorkspaceId(null);
    }
  };

  const handleSwitchWorkspace = (workspace: WorkspaceListItem) => {
    if (hasActiveTimeTracker) {
      setWorkspacePendingDiscard(workspace);
      return;
    }

    void executeSwitchWorkspace(workspace);
  };

  return (
    <>
      <SpeedDialMenu
        ariaLabel="Switch workspace"
        direction={speedDialDirection}
        triggerMode="click"
        closeOnActionSelect
        trigger={
          <span
            className={`${styles.triggerContent} ${!isSideNavExpanded ? styles.triggerContentCollapsed : ''}`}
          >
            <Avatar
              name={currentWorkspaceName}
              src={currentWorkspaceAvatarSource}
              size="medium"
              sx={{
                borderRadius: '50% !important',
              }}
            />
            {isSideNavExpanded ? (
              <span className={`truncate-sm ${styles.triggerLabel}`}>
                {currentWorkspaceName}
              </span>
            ) : null}
          </span>
        }
        actions={otherWorkspaces.map((workspace) => ({
          key: workspace.id,
          label: workspace.name,
          icon: (
            <Tooltip title={workspace.name} placement="top">
              <Avatar
                name={workspace.name}
                src={getWorkspaceAvatarSource(workspace)}
                size="medium"
                sx={{
                  borderRadius: '50% !important',
                }}
              />
            </Tooltip>
          ),
          hideTooltip: true,
          disabled:
            isFetching ||
            isSwitchingWorkspace ||
            pendingWorkspaceId === workspace.id,
          onClick: () => {
            handleSwitchWorkspace(workspace);
          },
        }))}
        fabProps={{
          variant: isSideNavExpanded ? 'extended' : 'circular',
          disabled: isFetching || isSwitchingWorkspace,
        }}
        speedDialSx={{
          position: 'static',
          '& .MuiFab-root': {
            alignItems: 'center',
            border: 'none !important',
            borderRadius: 'var(--inner-border-radius)',
            boxShadow: 'none',
            color: 'var(--nn-color-text)',
            display: 'inline-flex',
            justifyContent: isSideNavExpanded ? 'flex-start' : 'center',
            minHeight: '44px',
            minWidth: isSideNavExpanded ? '100%' : '44px',
            padding: isSideNavExpanded ? '0.5rem' : '0',
            width: isSideNavExpanded ? '100%' : '44px',
          },
        }}
      />

      <ConfirmActionModal
        open={Boolean(workspacePendingDiscard)}
        title="Switch workspace?"
        description="Switching workspace will discard the active timer for this workspace."
        confirmButtonLabel="Discard timer and switch"
        cancelButtonLabel="Stay here"
        requireConfirmationText={false}
        onClose={() => setWorkspacePendingDiscard(null)}
        onConfirm={() => {
          const nextWorkspace = workspacePendingDiscard;
          setWorkspacePendingDiscard(null);
          discardPersistedTimeTracker(currentWorkspace?.id);
          if (nextWorkspace) {
            void executeSwitchWorkspace(nextWorkspace);
          }
        }}
      />
    </>
  );
}
